import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import Address from '@afosto/app-components-react/Address';
import AddressSelectionDrawer from '@afosto/app-components-react/AddressSelectionDrawer';
import getFullName from '@afosto/utils/getFullName';
import translations from './translations';

const OrderAddressFormSection = ({
  address,
  availableAddresses,
  contact,
  disableAddressSelection,
  onChange,
  onUpdateContact,
  onUpdateOrganisation,
  organisation,
  showSetAsBillingAddress,
  showSetAsShippingAddress,
  title,
  type,
}) => {
  const intl = useIntl();
  const [showSelectAddress, setShowSelectAddress] = useState(false);

  /**
   * Handle select address.
   * @param {object} selectedAddress
   */
  const handleSelectAddress = selectedAddress => {
    if (onChange && typeof onChange === 'function') {
      onChange({}, selectedAddress);
    }
    setShowSelectAddress(false);
  };

  /**
   * Handler fired after creating a new contact address
   * @param {object} createdAddress
   * @param {object} updatedContact
   */
  const handleCreateContactAddress = (createdAddress, updatedContact) => {
    if (onUpdateContact && typeof onUpdateContact === 'function') {
      onUpdateContact(
        {},
        { ...updatedContact, label: getFullName(updatedContact), value: updatedContact?.id },
      );
    }

    if (onChange && typeof onChange === 'function') {
      onChange({}, createdAddress);
    }
  };

  /**
   * Handler fired after creating a new organisation address
   * @param {object} createdAddress
   * @param {object} updatedOrganisation
   */
  const handleCreateOrganisationAddress = (createdAddress, updatedOrganisation) => {
    if (onUpdateOrganisation && typeof onUpdateOrganisation === 'function') {
      onUpdateOrganisation({}, updatedOrganisation);
    }

    if (onChange && typeof onChange === 'function') {
      onChange({}, createdAddress);
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={title}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setShowSelectAddress(true)}
              disabled={disableAddressSelection}
            >
              {intl.formatMessage(translations.button.select.label)}
            </Button>
          }
        />
        <CardContent>
          {address && (
            <Address
              company={address.organisation}
              fullName={getFullName(address)}
              addressLine1={address.address_line_1}
              addressLine2={address.address_line_2}
              postalCode={address.postal_code}
              city={address.locality}
              country={address.country_code}
              administrativeArea={address.administrative_area}
            />
          )}

          {!address && (
            <Typography variant="body2">
              {intl.formatMessage(translations.emptyMessage)}.
            </Typography>
          )}
        </CardContent>
      </Card>
      <AddressSelectionDrawer
        isOpen={showSelectAddress}
        onClose={() => setShowSelectAddress(false)}
        contact={contact ? { ...contact, ...availableAddresses?.contact } : null}
        organisation={
          organisation ? { ...organisation, ...availableAddresses?.organisation } : null
        }
        selectedAddress={address}
        setSelectedAddress={handleSelectAddress}
        onCreateContactAddress={handleCreateContactAddress}
        onCreateOrganisationAddress={handleCreateOrganisationAddress}
        showSetAsBillingAddress={showSetAsBillingAddress}
        showSetAsShippingAddress={showSetAsShippingAddress}
        type={type}
      />
    </>
  );
};

OrderAddressFormSection.propTypes = {
  address: PropTypes.object,
  availableAddresses: PropTypes.object,
  contact: PropTypes.object,
  organisation: PropTypes.object,
  disableAddressSelection: PropTypes.bool,
  onChange: PropTypes.func,
  onUpdateContact: PropTypes.func,
  onUpdateOrganisation: PropTypes.func,
  showSetAsBillingAddress: PropTypes.bool,
  showSetAsShippingAddress: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.string,
};

OrderAddressFormSection.defaultProps = {
  address: undefined,
  availableAddresses: undefined,
  contact: null,
  organisation: null,
  disableAddressSelection: false,
  onChange: undefined,
  onUpdateContact: undefined,
  onUpdateOrganisation: undefined,
  showSetAsBillingAddress: true,
  showSetAsShippingAddress: true,
  title: undefined,
  type: 'billing',
};

export default OrderAddressFormSection;
